import { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { wsPipeline } from "@/lib/workspace";
import Header from "@/components/Header";
import BrandFooter from "@/components/BrandFooter";
import { toast } from "sonner";
import { Save, Plus, Trash2, UserPlus, Camera, Link2 } from "lucide-react";
import Avatar from "@/components/Avatar";
import { useAuth } from "@/context/AuthContext";
import { isOwner } from "@/lib/roles";
import { EmailTemplatesAdmin } from "@/components/settings/EmailTemplatesAdmin";
import { GmailSetup } from "@/components/settings/GmailSetup";
import { AssignEmailedCard } from "@/components/settings/AssignEmailedCard";
import { useSearchParams } from "react-router-dom";

const ROLE_OPTIONS = [
  { value: "owner", label: "owner (super-admin)" },
  { value: "manager", label: "manager" },
  { value: "staff", label: "staff" },
  { value: "viewer", label: "viewer (read-only)" },
];

// Downscale an image File to a JPEG data URL (keeps payloads small before upload).
function resizeImageToDataUrl(file, max = 320) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function SectionTitle({ children, sub }) {
  return (
    <div className="mb-3">
      <h2 className="text-xl font-extrabold tracking-tight text-[#15233B]">{children}</h2>
      {sub && <p className="text-xs text-[#15233B]/60 mt-0.5">{sub}</p>}
    </div>
  );
}

function ListEditor({ label, title, value, onChange, testId }) {
  const [items, setItems] = useState(value || []);
  useEffect(() => setItems(value || []), [value]);
  const update = (i, v) => setItems((p) => p.map((x, idx) => (idx === i ? v : x)));
  const remove = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
  const add = () => setItems((p) => [...p, ""]);
  const save = async () => {
    const cleaned = items.map((s) => s.trim()).filter(Boolean);
    try {
      await api.patch("/lists", { [label]: cleaned });
      toast.success(`${title || label} saved`);
      onChange(cleaned);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div data-testid={testId} className="bg-white border border-[#15233B]/10 rounded-md p-4">
      <div className="flex justify-between items-center mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">{title || label.replace(/_/g, " ")}</div>
        <button onClick={save} className="text-xs font-semibold text-[#FBBF24] hover:underline flex items-center gap-1">
          <Save className="h-3 w-3" /> Save
        </button>
      </div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2">
            <input
              value={it}
              onChange={(e) => update(i, e.target.value)}
              className="flex-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
            />
            <button onClick={() => remove(i)} className="p-1.5 rounded text-[#b91c1c] hover:bg-[#b91c1c]/5">
              <Trash2 className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
      <button onClick={add} className="mt-2 text-xs font-semibold text-[#15233B] hover:underline flex items-center gap-1">
        <Plus className="h-3 w-3" /> Add
      </button>
    </div>
  );
}

function TemplateEditor({ tpl, users, onSaved }) {
  const [tasks, setTasks] = useState(tpl.tasks);
  const [dayTarget, setDayTarget] = useState(tpl.day_target);
  const [ownerId, setOwnerId] = useState(tpl.default_owner_id || "");
  const [reqNextAction, setReqNextAction] = useState(!!tpl.requires_next_action);
  const [reqPa, setReqPa] = useState(!!tpl.requires_pa_expires_on);
  const [reqDhs, setReqDhs] = useState(!!tpl.requires_dhs12_cleared);
  const update = (i, v) => setTasks((p) => p.map((x, idx) => (idx === i ? v : x)));
  const remove = (i) => setTasks((p) => p.filter((_, idx) => idx !== i));
  const add = () => setTasks((p) => [...p, "New task"]);
  const save = async () => {
    try {
      const { data } = await api.patch(`/templates/${tpl.stage_number}`, {
        tasks: tasks.map((t) => t.trim()).filter(Boolean),
        day_target: Number(dayTarget),
        default_owner_id: ownerId || null,
        requires_next_action: reqNextAction,
        requires_pa_expires_on: reqPa,
        requires_dhs12_cleared: reqDhs,
      });
      onSaved(data);
      toast.success(`Stage ${tpl.stage_number} saved`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div data-testid={`template-${tpl.stage_number}`} className="bg-white border border-[#15233B]/10 rounded-md p-4">
      <div className="flex items-center gap-3 mb-3">
        <span className="text-[10px] font-mono text-[#15233B]/50">{String(tpl.stage_number).padStart(2, "0")}</span>
        <h3 className="text-sm font-extrabold tracking-tight text-[#15233B]">{tpl.name}</h3>
        {tpl.skippable && <span className="text-[10px] uppercase tracking-[0.15em] text-[#FBBF24] font-bold">skippable</span>}
        <button onClick={save} className="ml-auto text-xs font-semibold text-[#FBBF24] hover:underline flex items-center gap-1">
          <Save className="h-3 w-3" /> Save
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Day target</label>
          <input type="number" min="1" value={dayTarget} onChange={(e) => setDayTarget(e.target.value)} className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Default owner</label>
          <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white">
            <option value="">— none —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>
      <div className="mb-3 border border-[#FFCB05]/30 bg-[#FFCB05]/5 rounded p-3">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 mb-2">Required-field gates (block advance until satisfied)</div>
        <div className="flex flex-wrap gap-4 text-xs">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={reqNextAction} onChange={(e) => setReqNextAction(e.target.checked)} data-testid={`gate-next-${tpl.stage_number}`} />
            Next-action date must be set
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={reqPa} onChange={(e) => setReqPa(e.target.checked)} data-testid={`gate-pa-${tpl.stage_number}`} />
            PA expiration must be recorded
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={reqDhs} onChange={(e) => setReqDhs(e.target.checked)} data-testid={`gate-dhs-${tpl.stage_number}`} />
            DHS-12 must be cleared
          </label>
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Tasks</label>
        <ul className="mt-2 space-y-2">
          {tasks.map((t, i) => (
            <li key={i} className="flex gap-2">
              <input value={t} onChange={(e) => update(i, e.target.value)} className="flex-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white" />
              <button onClick={() => remove(i)} className="p-1.5 rounded text-[#b91c1c] hover:bg-[#b91c1c]/5"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
        <button onClick={add} className="mt-2 text-xs font-semibold text-[#15233B] hover:underline flex items-center gap-1"><Plus className="h-3 w-3" /> Add task</button>
      </div>
    </div>
  );
}

function UsersAdmin({ users, setUsers }) {
  const [newU, setNewU] = useState({ name: "", email: "", role: "staff", password: "" });
  const [resetFor, setResetFor] = useState(null); // user obj
  const [resetPw, setResetPw] = useState("");
  const create = async () => {
    if (!newU.name || !newU.email || !newU.password) {
      toast.error("Name, email, password required");
      return;
    }
    try {
      const { data } = await api.post("/users", newU);
      setUsers((p) => [...p, data]);
      setNewU({ name: "", email: "", role: "staff", password: "" });
      toast.success("User created");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const del = async (id) => {
    if (!confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      setUsers((p) => p.filter((u) => u.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleDigest = async (u) => {
    try {
      const { data } = await api.patch(`/users/${u.id}`, { digest_enabled: !u.digest_enabled });
      setUsers((p) => p.map((x) => (x.id === u.id ? data : x)));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const submitReset = async () => {
    if (resetPw.length < 6) return toast.error("Password must be at least 6 characters");
    try {
      await api.post(`/users/${resetFor.id}/reset-password`, { new_password: resetPw });
      toast.success(`Password reset for ${resetFor.name}`);
      setResetFor(null); setResetPw("");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const uploadAvatar = async (u, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    try {
      const dataUrl = await resizeImageToDataUrl(file, 320);
      const { data } = await api.post(`/users/${u.id}/avatar`, { image: dataUrl });
      setUsers((p) => p.map((x) => (x.id === u.id ? data : x)));
      toast.success(`Photo updated for ${u.name}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeAvatar = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/avatar`, { image: "" });
      setUsers((p) => p.map((x) => (x.id === u.id ? data : x)));
      toast.success(`Photo removed for ${u.name}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const changeRole = async (u, role) => {
    try {
      const { data } = await api.patch(`/users/${u.id}`, { role });
      setUsers((p) => p.map((x) => (x.id === u.id ? data : x)));
      toast.success(`${u.name} is now ${role}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid="users-admin" className="bg-white border border-[#15233B]/10 rounded-md p-4">
      <h3 className="text-sm font-extrabold tracking-tight text-[#15233B] uppercase tracking-[0.15em] mb-3">Users</h3>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 bg-[#F6F3EC]">
          <tr><th className="text-left px-3 py-2">Photo</th><th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Email</th><th className="text-left px-3 py-2">Role</th><th className="text-left px-3 py-2">Digest</th><th className="text-left px-3 py-2">Last sign-in</th><th></th></tr>
        </thead>
        <tbody className="divide-y divide-[#15233B]/8">
          {users.map((u) => (
            <tr key={u.id}>
              <td className="px-3 py-2">
                <div className="flex items-center gap-3">
                  <Avatar user={u} size={40} />
                  <div className="flex flex-col gap-1">
                    <label
                      className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#15233B] bg-[#FFCB05] hover:bg-[#FBC02D] rounded-md px-2.5 py-1.5 cursor-pointer transition-colors w-fit"
                      data-testid={`avatar-upload-label-${u.email}`}
                    >
                      <Camera className="h-3.5 w-3.5" />
                      {u.avatar ? "Change photo" : "Upload photo"}
                      <input
                        type="file"
                        accept="image/*"
                        data-testid={`avatar-upload-${u.email}`}
                        className="hidden"
                        onChange={(e) => { uploadAvatar(u, e.target.files?.[0]); e.target.value = ""; }}
                      />
                    </label>
                    {u.avatar && (
                      <button onClick={() => removeAvatar(u)} data-testid={`avatar-remove-${u.email}`} className="text-[10px] text-[#b91c1c] hover:underline text-left">Remove photo</button>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-3 py-2 font-semibold">{u.name}</td>
              <td className="px-3 py-2 text-xs font-mono text-[#15233B]/70">{u.email}</td>
              <td className="px-3 py-2 text-xs">
                <select
                  data-testid={`role-select-${u.email}`}
                  value={u.role}
                  onChange={(e) => changeRole(u, e.target.value)}
                  className="border border-[#15233B]/15 rounded px-1.5 py-1 text-xs bg-white"
                >
                  {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </td>
              <td className="px-3 py-2">
                <button onClick={() => toggleDigest(u)} className={`text-xs font-bold px-2 py-1 rounded ${u.digest_enabled ? "bg-[#166534]/10 text-[#166534]" : "bg-[#15233B]/5 text-[#15233B]/60"}`}>
                  {u.digest_enabled ? "On" : "Off"}
                </button>
              </td>
              <td className="px-3 py-2" data-testid={`last-login-${u.email}`}>
                {u.last_login_at ? (
                  <span className="text-xs font-semibold text-[#15233B]/75" title={new Date(u.last_login_at).toLocaleString()}>
                    {new Date(u.last_login_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                  </span>
                ) : (
                  <span className="text-xs font-bold px-2 py-0.5 rounded bg-[#b91c1c]/10 text-[#b91c1c]">Never</span>
                )}
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                <button
                  data-testid={`reset-pw-${u.email}`}
                  onClick={() => setResetFor(u)}
                  className="text-xs font-semibold text-[#FBBF24] hover:underline mr-3"
                >
                  Reset password
                </button>
                <button onClick={() => del(u.id)} className="text-xs text-[#b91c1c] hover:underline">Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
        <input placeholder="Name" value={newU.name} onChange={(e) => setNewU({ ...newU, name: e.target.value })} className="border border-[#15233B]/15 rounded px-2 py-1.5 text-sm" />
        <input placeholder="Email" value={newU.email} onChange={(e) => setNewU({ ...newU, email: e.target.value })} className="border border-[#15233B]/15 rounded px-2 py-1.5 text-sm" />
        <select value={newU.role} onChange={(e) => setNewU({ ...newU, role: e.target.value })} className="border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white">
          {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <input placeholder="Password" type="password" value={newU.password} onChange={(e) => setNewU({ ...newU, password: e.target.value })} className="border border-[#15233B]/15 rounded px-2 py-1.5 text-sm" />
        <button onClick={create} className="px-3 py-2 rounded bg-[#FBBF24] text-white text-xs font-bold flex items-center justify-center gap-1"><UserPlus className="h-3 w-3" /> Add user</button>
      </div>

      {resetFor && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="admin-reset-pw-dialog">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-[#15233B]/10 bg-[#F6F3EC]">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#15233B]/60">Admin reset</div>
              <h2 className="text-lg font-extrabold tracking-tight text-[#15233B]">{resetFor.name}</h2>
              <div className="text-xs text-[#15233B]/60 font-mono">{resetFor.email}</div>
            </div>
            <div className="p-6 space-y-3">
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">New password</label>
              <input
                data-testid="admin-reset-pw-input"
                type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)}
                className="w-full border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white"
                placeholder="At least 6 characters"
              />
              <div className="text-[11px] italic text-[#15233B]/50">
                The user can change it themselves under their profile menu.
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#15233B]/10 flex justify-end gap-2">
              <button onClick={() => { setResetFor(null); setResetPw(""); }} className="px-3 py-2 rounded text-sm font-semibold text-[#15233B] hover:bg-[#15233B]/5">Cancel</button>
              <button data-testid="admin-reset-pw-submit" onClick={submitReset} className="px-4 py-2 rounded bg-[#b91c1c] text-white text-sm font-bold hover:bg-[#9b1818]">Reset password</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EffortRow({ effort, onSaved, onDeleted }) {
  const [name, setName] = useState(effort.name);
  const [type, setType] = useState(effort.type);
  const [date, setDate] = useState(effort.date || "");
  const [notes, setNotes] = useState(effort.notes || "");
  const [active, setActive] = useState(!!effort.active);
  const [busy, setBusy] = useState(false);

  const dirty =
    name !== effort.name || type !== effort.type ||
    (date || "") !== (effort.date || "") || (notes || "") !== (effort.notes || "") ||
    active !== !!effort.active;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/marketing-efforts/${effort.id}`, {
        name, type, date: date || null, notes, active,
      });
      toast.success("Saved");
      onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${effort.name}"? Existing leads keep their record but lose the live link.`)) return;
    try {
      await api.delete(`/marketing-efforts/${effort.id}`);
      toast.success("Deleted");
      onDeleted(effort.id);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <tr className="border-b border-[#15233B]/10 align-top" data-testid={`effort-row-${effort.id}`}>
      <td className="py-2 pr-2">
        <input value={name} onChange={(e) => setName(e.target.value)}
          className="w-full border border-[#15233B]/15 rounded px-2 py-1 text-sm bg-white" />
      </td>
      <td className="py-2 px-2">
        <select value={type} onChange={(e) => setType(e.target.value)}
          className="border border-[#15233B]/15 rounded px-2 py-1 text-sm bg-white">
          <option value="event">Event</option>
          <option value="campaign">Campaign</option>
        </select>
      </td>
      <td className="py-2 px-2">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="border border-[#15233B]/15 rounded px-2 py-1 text-sm bg-white" />
      </td>
      <td className="py-2 px-2">
        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="—"
          className="w-full border border-[#15233B]/15 rounded px-2 py-1 text-sm bg-white" />
      </td>
      <td className="py-2 px-2 text-center">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)}
          className="h-4 w-4 rounded border-[#15233B]/30 text-[#FBBF24] focus:ring-[#FBBF24]" />
      </td>
      <td className="py-2 pl-2 whitespace-nowrap text-right">
        <button onClick={save} disabled={!dirty || busy}
          className="text-xs font-semibold text-[#FBBF24] hover:underline disabled:opacity-40 disabled:no-underline inline-flex items-center gap-1">
          <Save className="h-3 w-3" /> Save
        </button>
        <button onClick={remove} data-testid={`effort-delete-${effort.id}`}
          className="ml-3 p-1 rounded text-[#b91c1c] hover:bg-[#b91c1c]/5 inline-flex">
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
}

function MarketingEffortsAdmin() {
  const [efforts, setEfforts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", type: "event", date: "", notes: "", active: true });

  useEffect(() => {
    api.get("/marketing-efforts").then(({ data }) => { setEfforts(data || []); setLoading(false); });
  }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      const { data } = await api.post("/marketing-efforts", {
        name: form.name.trim(), type: form.type, date: form.date || null, notes: form.notes, active: form.active,
      });
      setEfforts((p) => [data, ...p]);
      setForm({ name: "", type: "event", date: "", notes: "", active: true });
      toast.success("Created");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="marketing-efforts-admin" className="bg-white border border-[#15233B]/10 rounded-md p-4">
      <h3 className="text-sm font-extrabold tracking-tight text-[#15233B] uppercase tracking-[0.15em] mb-1">Events &amp; Campaigns</h3>
      <p className="text-xs text-[#15233B]/60 mb-4">
        Create named events and campaigns. They appear as choices when a lead&apos;s referral source is <b>Event</b> or <b>Campaign</b>, so you can track which efforts bring in leads.
      </p>

      {/* Add new */}
      <div className="grid grid-cols-12 gap-2 items-end mb-4 bg-[#F6F3EC] rounded-md p-3">
        <div className="col-span-12 sm:col-span-4">
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Name</label>
          <input data-testid="effort-new-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g., Summer Health Fair 2025"
            className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white" />
        </div>
        <div className="col-span-6 sm:col-span-2">
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Type</label>
          <select data-testid="effort-new-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white">
            <option value="event">Event</option>
            <option value="campaign">Campaign</option>
          </select>
        </div>
        <div className="col-span-6 sm:col-span-2">
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Date</label>
          <input type="date" data-testid="effort-new-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white" />
        </div>
        <div className="col-span-9 sm:col-span-3">
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">Notes</label>
          <input data-testid="effort-new-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="optional"
            className="w-full mt-1 border border-[#15233B]/15 rounded px-2 py-1.5 text-sm bg-white" />
        </div>
        <div className="col-span-3 sm:col-span-1">
          <button onClick={create} data-testid="effort-create"
            className="w-full px-3 py-2 rounded bg-[#FFCB05] text-[#15233B] text-sm font-extrabold hover:bg-[#FBC02D] inline-flex items-center justify-center gap-1">
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#15233B]/50">Loading…</div>
      ) : efforts.length === 0 ? (
        <div className="text-sm text-[#15233B]/50 italic">No events or campaigns yet. Add one above.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#15233B]/60">
              <tr className="border-b border-[#15233B]/10">
                <th className="text-left py-2 pr-2">Name</th>
                <th className="text-left py-2 px-2">Type</th>
                <th className="text-left py-2 px-2">Date</th>
                <th className="text-left py-2 px-2">Notes</th>
                <th className="text-center py-2 px-2">Active</th>
                <th className="text-right py-2 pl-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {efforts.map((e) => (
                <EffortRow key={e.id} effort={e}
                  onSaved={(d) => setEfforts((p) => p.map((x) => (x.id === d.id ? d : x)))}
                  onDeleted={(id) => setEfforts((p) => p.filter((x) => x.id !== id))} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function DataCleanupAdmin() {
  const [batches, setBatches] = useState(null);
  const [activeTotal, setActiveTotal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDate, setConfirmDate] = useState(null);

  const load = () => {
    api.get("/admin/bulk-import-batches").then(({ data }) => {
      setBatches(data.batches);
      setActiveTotal(data.active_total);
    }).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(load, []);

  const runCleanup = async (date) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/bulk-import-cleanup", { date });
      toast.success(`Removed ${data.removed} imported leads — ${data.active_total} active remain`);
      setConfirmDate(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (batches === null) return <div className="text-sm text-[#15233B]/60">Loading…</div>;

  return (
    <div data-testid="data-cleanup-section" className="max-w-3xl space-y-4">
      <div className="bg-white border border-[#15233B]/10 rounded-md p-4">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 mb-1">Import / burst cleanup</div>
        <p className="text-xs text-[#15233B]/60">
          Lists every day where 8+ leads were created at once (imports, scripts or direct database inserts).
          Removal only touches leads with <span className="font-semibold">no activity after that day</span> — anything your team
          has since worked on is always kept. Your original data batch will also appear here: check the date and
          review the list before removing anything.
          Currently <span className="font-bold text-[#15233B]" data-testid="cleanup-active-total">{activeTotal}</span> active leads.
        </p>
      </div>
      {batches.length === 0 && (
        <div data-testid="cleanup-no-batches" className="bg-white border border-[#15233B]/10 rounded-md p-4 text-sm text-[#15233B]/60">
          No creation bursts found — nothing to clean up.
        </div>
      )}
      {batches.map((b) => (
        <div key={b.date} data-testid={`cleanup-batch-${b.date}`} className="bg-white border border-[#15233B]/10 rounded-md p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm font-extrabold text-[#15233B]">
                {b.date} — {b.imported} leads created{b.imported_by.length > 0 ? ` by ${b.imported_by.join(", ")}` : ""}
              </div>
              {b.sources.length > 0 && (
                <div className="text-[11px] text-[#15233B]/50 mt-0.5">via: {b.sources.join(" · ")}</div>
              )}
              <div className="text-xs text-[#15233B]/60 mt-0.5">
                {b.removable.length} removable ({b.removable.filter((c) => c.duplicate).length} duplicates of existing clients)
                {b.touched.length > 0 && ` · ${b.touched.length} kept (activity after ${b.date})`}
              </div>
            </div>
            {b.removable.length > 0 && (confirmDate === b.date ? (
              <div className="flex items-center gap-2">
                <button
                  data-testid={`cleanup-confirm-${b.date}`}
                  disabled={busy}
                  onClick={() => runCleanup(b.date)}
                  className="px-3 py-2 rounded-md bg-[#b91c1c] text-white text-xs font-bold disabled:opacity-50"
                >
                  {busy ? "Removing…" : `Yes, permanently remove ${b.removable.length}`}
                </button>
                <button data-testid={`cleanup-cancel-${b.date}`} onClick={() => setConfirmDate(null)} className="px-3 py-2 rounded-md border border-[#15233B]/15 text-xs font-semibold">
                  Cancel
                </button>
              </div>
            ) : (
              <button
                data-testid={`cleanup-remove-${b.date}`}
                onClick={() => setConfirmDate(b.date)}
                className="px-3 py-2 rounded-md border border-[#b91c1c]/40 text-[#b91c1c] hover:bg-[#b91c1c]/5 text-xs font-bold flex items-center gap-1"
              >
                <Trash2 className="h-3.5 w-3.5" /> Remove {b.removable.length} imported leads
              </button>
            ))}
          </div>
          {b.removable.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs font-semibold text-[#1E73C9] cursor-pointer">Show the {b.removable.length} leads that would be removed</summary>
              <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-[#15233B]/80">
                {b.removable.map((c) => (
                  <li key={c.id}>{c.av_id} · {c.full_name}{c.duplicate ? " (duplicate)" : ""}</li>
                ))}
              </ul>
            </details>
          )}
          {b.touched.length > 0 && (
            <details className="mt-2">
              <summary className="text-xs font-semibold text-[#15233B]/50 cursor-pointer">Kept — activity after {b.date} ({b.touched.length})</summary>
              <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-[#15233B]/60">
                {b.touched.map((c) => (
                  <li key={c.id}>{c.av_id} · {c.full_name} (stage {c.current_stage})</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      ))}
    </div>
  );
}

function ApplyKeyCard() {
  const [info, setInfo] = useState(null);
  const [rotating, setRotating] = useState(false);
  useEffect(() => {
    api.get("/admin/apply-key").then(({ data }) => setInfo(data)).catch(() => {});
  }, []);
  if (!info) return null;
  const url = `${window.location.origin}/api/public/apply`;
  const rotate = async () => {
    if (!window.confirm("Generate a brand-new key?\n\nYour website will STOP creating candidates until you update the key in your form (or Supabase secret). Only do this once you're ready to swap it.")) return;
    setRotating(true);
    try {
      const { data } = await api.post("/admin/apply-key/rotate");
      setInfo(data);
      toast.success("New key generated — update it in your website/Supabase now");
    } catch {
      toast.error("Could not regenerate key");
    } finally {
      setRotating(false);
    }
  };
  return (
    <div data-testid="apply-key-card" className="bg-white border border-[#15233B]/10 rounded-md p-3 mb-6 max-w-3xl">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60 mb-1">Website applications (avidahealth.org/apply)</div>
      <p className="text-xs text-[#15233B]/60 mb-2">
        Your website can POST applications here to create candidates automatically. Keep the key secret.
      </p>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <code className="bg-[#15233B]/5 px-2 py-1 rounded font-mono">{url}</code>
        <code className="bg-[#15233B]/5 px-2 py-1 rounded font-mono" data-testid="apply-key-value">X-Apply-Key: {info.key}</code>
        <button
          data-testid="apply-key-copy"
          onClick={() => { navigator.clipboard.writeText(`${url} | X-Apply-Key: ${info.key}`); toast.success("Copied"); }}
          className="px-2 py-1 rounded border border-[#1E73C9]/40 text-[#1E73C9] font-bold hover:bg-[#1E73C9]/5"
        >
          Copy
        </button>
        <button
          data-testid="apply-key-rotate"
          onClick={rotate}
          disabled={rotating}
          className="px-2 py-1 rounded border border-[#b91c1c]/40 text-[#b91c1c] font-bold hover:bg-[#b91c1c]/5 disabled:opacity-50"
        >
          {rotating ? "Regenerating…" : "Regenerate"}
        </button>
      </div>
    </div>
  );
}

function BackupHealthBadge() {
  const [health, setHealth] = useState(null);
  useEffect(() => {
    api.get("/admin/backup-health").then(({ data }) => setHealth(data)).catch(() => setHealth(false));
  }, []);
  if (health === null || health === false) return null;

  const chip = (label, s, detail) => {
    const ok = s === "ok";
    const gray = s === "not_configured";
    const cls = ok
      ? "bg-[#16A34A]/10 border-[#16A34A]/40 text-[#166534]"
      : gray
        ? "bg-[#15233B]/5 border-[#15233B]/15 text-[#15233B]/60"
        : "bg-[#b91c1c]/10 border-[#b91c1c]/40 text-[#b91c1c]";
    const dot = ok ? "bg-[#16A34A]" : gray ? "bg-[#15233B]/30" : "bg-[#b91c1c] animate-pulse";
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-[11px] font-bold ${cls}`}>
        <span className={`h-2 w-2 rounded-full ${dot}`} />
        {label}: {ok ? "OK" : gray ? "not set up" : s}
        {detail && <span className="font-medium opacity-75">· {detail}</span>}
      </span>
    );
  };

  const l = health.local;
  const o = health.offsite;
  return (
    <div data-testid="backup-health-badge" className="flex flex-wrap items-center gap-2 mb-6">
      <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/50">Backup health</span>
      <span data-testid="backup-health-local">
        {chip("Nightly backup", l.status, l.status === "ok" ? `${l.age_hours}h ago · ${l.size_mb} MB` : l.status === "stale" ? `last ${l.age_hours}h ago` : "no backup file found")}
      </span>
      <span data-testid="backup-health-offsite">
        {chip("Offsite copy", o.status, o.status === "ok" ? `${o.age_hours}h ago` : o.status === "error" ? (o.last_error || "").slice(0, 60) : o.status === "stale" ? "over 26h old" : null)}
      </span>
    </div>
  );
}

function AvidaCareAdmin() {
  const [cfg, setCfg] = useState(null);
  const [url, setUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/avida-care")
      .then(({ data }) => { setCfg(data); setUrl(data.url || ""); setEnabled(!!data.enabled); })
      .catch(() => setCfg({ error: true }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { url, enabled };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const { data } = await api.put("/settings/avida-care", body);
      setCfg(data); setApiKey("");
      toast.success("Avida Care settings saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  if (!cfg) return <div className="text-sm text-[#15233B]/60">Loading…</div>;
  if (cfg.error) return <div className="text-sm text-[#b91c1c]">Only owners can manage integrations.</div>;

  return (
    <div data-testid="avida-care-admin" className="bg-white border border-[#15233B]/10 rounded-md p-5 max-w-2xl space-y-4">
      <div>
        <h3 className="text-sm font-extrabold tracking-[0.15em] uppercase text-[#15233B] flex items-center gap-2"><Link2 className="h-4 w-4 text-[#1E73C9]" /> Avida Care platform</h3>
        <p className="text-xs text-[#15233B]/60 mt-1">
          Push a client to your Avida Care app with the &ldquo;Send to Avida Care&rdquo; button on a card (available once a client reaches the Screening stage).
        </p>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">API endpoint URL</label>
        <input data-testid="avida-care-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://app.avidacare.example/api/intake/clients"
          className="w-full mt-1 border border-[#15233B]/15 rounded px-2.5 py-2 text-sm bg-white" />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60">API key</label>
        <input data-testid="avida-care-key" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
          placeholder={cfg.key_set ? "•••••••• (saved — leave blank to keep)" : "Paste the key from your Avida Care developer"}
          className="w-full mt-1 border border-[#15233B]/15 rounded px-2.5 py-2 text-sm bg-white font-mono" />
        <div className="mt-1 text-[10px] text-[#15233B]/55">Sent as <code>Authorization: Bearer &lt;key&gt;</code>. {cfg.key_set ? "A key is currently saved." : "No key saved yet."}</div>
      </div>
      <label className="flex items-center gap-2 text-sm font-semibold text-[#15233B]">
        <input data-testid="avida-care-enabled" type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4" />
        Enable sending to Avida Care
      </label>
      <button data-testid="avida-care-save" onClick={save} disabled={saving}
        className="px-4 py-2 rounded bg-[#1E73C9] text-white text-sm font-bold hover:bg-[#1565C0] disabled:opacity-60 flex items-center gap-1">
        <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
      </button>
      <div className="mt-2 rounded-md bg-[#F6F3EC] border border-[#15233B]/10 p-3 text-[11px] text-[#15233B]/70 leading-relaxed">
        <div className="font-bold text-[#15233B] mb-1">What to ask your Avida Care developer for:</div>
        <ul className="list-disc ml-4 space-y-0.5">
          <li>An HTTPS endpoint that accepts a <code>POST</code> with a JSON body (the full lead record).</li>
          <li>Authentication via an <code>Authorization: Bearer &lt;API_KEY&gt;</code> header — ask them to generate that key for you.</li>
          <li>On success, respond <code>200/201</code> with <code>{`{ "id": "<client id>" }`}</code> — we store that id so re-sends update instead of duplicating.</li>
          <li>De-dupe on our <code>av_id</code> / <code>id</code> field so repeated sends are idempotent.</li>
          <li>The payload contains PHI — the endpoint must be HTTPS and covered by your BAA.</li>
        </ul>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [lists, setLists] = useState({});
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "templates");
  const { user } = useAuth();

  useEffect(() => {
    Promise.all([api.get("/templates", { params: { pipeline: wsPipeline() } }), api.get("/users"), api.get("/lists")]).then(([t, u, l]) => {
      setTemplates(t.data); setUsers(u.data); setLists(l.data);
    });
  }, []);

  const tabs = [
    { id: "templates", label: "Stage templates" },
    { id: "lists", label: "Dropdown lists" },
    { id: "efforts", label: "Events & Campaigns" },
    { id: "users", label: "Users" },
    { id: "cleanup", label: "Data cleanup" },
    { id: "email", label: "Email templates" },
    ...(isOwner(user) ? [{ id: "integrations", label: "Integrations" }] : []),
  ];

  return (
    <div className="min-h-screen flex flex-col paper-bg">
      <Header />
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-6xl w-full mx-auto">
        <SectionTitle sub="Admin controls. Stage names are fixed; tasks, day targets and owners are editable.">Settings</SectionTitle>

        <BackupHealthBadge />

        <ApplyKeyCard />

        <div className="flex gap-2 mb-6 border-b border-[#15233B]/10">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`settings-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                tab === t.id ? "border-[#FBBF24] text-[#15233B]" : "border-transparent text-[#15233B]/60 hover:text-[#15233B]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "templates" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map((tpl) => (
              <TemplateEditor key={tpl.stage_number} tpl={tpl} users={users} onSaved={(data) => setTemplates((p) => p.map((x) => x.stage_number === data.stage_number ? data : x))} />
            ))}
          </div>
        )}

        {tab === "lists" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ListEditor testId="list-mco" label="mco_list" value={lists.mco_list} onChange={(v) => setLists({ ...lists, mco_list: v })} />
            <ListEditor testId="list-lost" label="lost_reasons" title="Client archive reasons" value={lists.lost_reasons} onChange={(v) => setLists({ ...lists, lost_reasons: v })} />
            <ListEditor testId="list-recruiting-lost" label="recruiting_lost_reasons" title="Recruiting archive reasons" value={lists.recruiting_lost_reasons} onChange={(v) => setLists({ ...lists, recruiting_lost_reasons: v })} />
            <ListEditor testId="list-caregivers" label="caregivers" value={lists.caregivers} onChange={(v) => setLists({ ...lists, caregivers: v })} />
          </div>
        )}

        {tab === "efforts" && <MarketingEffortsAdmin />}

        {tab === "users" && <UsersAdmin users={users} setUsers={setUsers} />}

        {tab === "cleanup" && (
          <div className="space-y-6">
            {isOwner(user) && <AssignEmailedCard />}
            <DataCleanupAdmin />
          </div>
        )}

        {tab === "email" && <EmailTemplatesAdmin />}

        {tab === "integrations" && (
          <div className="space-y-6">
            <GmailSetup />
            <AvidaCareAdmin />
          </div>
        )}
      </div>
      <BrandFooter />
    </div>
  );
}
